import { useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { createCost } from "../../api/contract/cost";
import CompanySearch from "../../components/contract/CompanySearch";
import MobileAttachmentBox from "../../components/common/papers/MobileAttachmentBox";

const EMPTY_FORM = {
  cost_type: "지출",
  company_name: "",
  construction_type: "",
  code: "",
  payment_date: "",
  expected_amount: "",
  actual_amount: "",
  memo: "",
  activity_ids: [],
};

const CONSTRUCTION_TYPE_DETAIL_LABELS = {
  GENERAL: "General (일반)",
  ARCHITECTURAL: "Architectural (건축)",
  STRUCTURAL: "Structural (구조)",
  CIVIL: "Civil (토목)",
  LANDSCAPING: "Landscaping (조경)",
  MECHANICAL: "Mechanical (기계)",
  ELECTRICAL: "Electrical (전기)",
  COMMUNICATION: "Communication (통신)",
  FIREFIGHTING: "Firefighting (소방)",
};

export default function Cost_new({ isOpen, onClose, projectId, onCreated }) {
  const params = useParams();
  const realProjectId = projectId || params.id;

  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const [activityInput, setActivityInput] = useState("");
  const attachmentRef = useRef(null);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    let newVal = value;
    if (type === "number") {
      newVal = value === "" ? "" : Number(value);
    }
    setForm((prev) => ({ ...prev, [name]: newVal }));
  };

  const handleCostTypeChange = (value) => {
    setForm((prev) => ({ ...prev, cost_type: value }));
  };

  const handleConstructionTypeChange = (e) => {
    setForm((prev) => ({ ...prev, construction_type: e.target.value }));
  };

  const handleCompanySelect = (company) => {
    setForm((prev) => ({
      ...prev,
      company_name: company?.name || "",
    }));
    setCompanyModalOpen(false);
  };

  const handleAddActivity = () => {
    const trimmed = activityInput.trim();
    if (!trimmed) return;
    const tokens = trimmed.split(/[, ]+/).filter(Boolean);
    setForm((prev) => ({
      ...prev,
      activity_ids: [...(prev.activity_ids || []), ...tokens],
    }));
    setActivityInput("");
  };

  const handleRemoveActivity = (idx) => {
    setForm((prev) => ({
      ...prev,
      activity_ids: prev.activity_ids.filter((_, i) => i !== idx),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!realProjectId) {
      alert("프로젝트 ID가 없습니다.");
      return;
    }

    if (!form.payment_date) {
      alert("지급일을 입력해주세요.");
      return;
    }
    if (!form.actual_amount && !form.expected_amount) {
      alert("계획 금액 또는 실제 금액 중 하나 이상을 입력해주세요.");
      return;
    }

    setLoading(true);
    try {
      const fd = new FormData();

      fd.append("project", realProjectId);
      fd.append("cost_type", form.cost_type || "지출");
      fd.append("company_name", form.company_name || "");
      fd.append("construction_type", form.construction_type || "");
      fd.append("code", form.code || "");
      fd.append("payment_date", form.payment_date || "");
      fd.append(
        "expected_amount",
        form.expected_amount ? String(form.expected_amount) : ""
      );
      fd.append(
        "actual_amount",
        form.actual_amount ? String(form.actual_amount) : ""
      );
      fd.append("memo", form.memo || "");

      if (form.activity_ids?.length) {
        form.activity_ids.forEach((id) =>
          fd.append("activity_ids", id.toString())
        );
      }

      const newCost = await createCost(realProjectId, fd);

      // 첨부파일 저장
      try {
        await attachmentRef.current?.save(newCost.dns);
      } catch (e) {
        console.warn("attachment 저장 에러 (비치명적):", e);
      }

      setForm(EMPTY_FORM);
      attachmentRef.current?.clear();
      setActivityInput("");

      if (onCreated && newCost?.dns) {
        onCreated(newCost.dns);
      }
      if (onClose) onClose();
    } catch (err) {
      console.error("기성 등록 실패:", err);
      const msg =
        err.response?.data?.detail || "저장에 실패했습니다. 다시 시도해주세요.";
      alert(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center px-3">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col min-w-0">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h2 className="text-base font-semibold">기성 등록·수정</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            닫기
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4">
          <div className="space-y-4 text-sm">
            {/* 지출/수익 타입 */}
            <div>
              <div className="text-xs font-medium text-gray-700 mb-1">
                비용 구분
              </div>
              <div className="inline-flex border rounded-lg overflow-hidden">
                {["지출", "수익"].map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => handleCostTypeChange(t)}
                    className={`px-3 py-1 text-xs font-medium ${
                      form.cost_type === t
                        ? "bg-blue-600 text-white"
                        : "bg-white text-gray-700"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {/* 공사 구분 */}
            <div>
              <div className="text-xs font-medium text-gray-700 mb-1">
                공사 구분
              </div>
              <select
                name="construction_type"
                value={form.construction_type}
                onChange={handleConstructionTypeChange}
                className="w-full border rounded-md px-2 py-1 text-sm"
              >
                <option value="">선택</option>
                {Object.entries(CONSTRUCTION_TYPE_DETAIL_LABELS).map(
                  ([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  )
                )}
              </select>
            </div>

            {/* 회사명 */}
            <div>
              <div className="text-xs font-medium text-gray-700 mb-1">
                회사명
              </div>
              <div className="flex gap-2 min-w-0">
                <input
                  type="text"
                  name="company_name"
                  value={form.company_name}
                  onChange={handleChange}
                  placeholder="회사명을 직접 입력하거나 검색 버튼을 이용하세요."
                  className="flex-1 border rounded-md px-2 py-1 text-sm min-w-0"
                />
                <button
                  type="button"
                  onClick={() => setCompanyModalOpen(true)}
                  className="btn-search-sm whitespace-nowrap flex-shrink-0"
                >
                  검색
                </button>
              </div>
            </div>

            {/* 코드 */}
            <div>
              <div className="text-xs font-medium text-gray-700 mb-1">코드</div>
              <input
                type="text"
                name="code"
                value={form.code}
                onChange={handleChange}
                className="w-full border rounded-md px-2 py-1 text-sm"
              />
            </div>

            {/* 지급일 */}
            <div>
              <div className="text-xs font-medium text-gray-700 mb-1">
                지급일
              </div>
              <input
                type="date"
                name="payment_date"
                value={form.payment_date}
                onChange={handleChange}
                className="w-full border rounded-md px-2 py-1 text-sm"
              />
            </div>

            {/* 금액 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <div className="text-xs font-medium text-gray-700 mb-1">
                  계획 금액
                </div>
                <input
                  type="number"
                  name="expected_amount"
                  value={form.expected_amount}
                  onChange={handleChange}
                  className="w-full border rounded-md px-2 py-1 text-sm"
                />
              </div>
              <div>
                <div className="text-xs font-medium text-gray-700 mb-1">
                  실제 금액
                </div>
                <input
                  type="number"
                  name="actual_amount"
                  value={form.actual_amount}
                  onChange={handleChange}
                  className="w-full border rounded-md px-2 py-1 text-sm"
                />
              </div>
            </div>

            {/* 활동 ID */}
            <div>
              <div className="text-xs font-medium text-gray-700 mb-1">
                Activity ID (여러 개 입력 시 쉼표 또는 공백 구분)
              </div>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={activityInput}
                  onChange={(e) => setActivityInput(e.target.value)}
                  className="flex-1 border rounded-md px-2 py-1 text-sm"
                  placeholder="예: ACT001, ACT002"
                />
                <button
                  type="button"
                  onClick={handleAddActivity}
                  className="btn-create-sm whitespace-nowrap"
                >
                  추가
                </button>
              </div>
              {form.activity_ids?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {form.activity_ids.map((id, idx) => (
                    <span
                      key={`${id}-${idx}`}
                      className="inline-flex items-center px-2 py-1 text-[11px] rounded-full bg-blue-50 text-blue-800"
                    >
                      {id}
                      <button
                        type="button"
                        onClick={() => handleRemoveActivity(idx)}
                        className="ml-1 text-xs text-blue-500 hover:text-blue-700"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* 메모 */}
            <div>
              <div className="text-xs font-medium text-gray-700 mb-1">메모</div>
              <textarea
                name="memo"
                value={form.memo}
                onChange={handleChange}
                rows={3}
                className="w-full border rounded-md px-2 py-1 text-sm resize-none"
              />
            </div>

            {/* 첨부파일 */}
            <div>
              <div className="text-xs font-medium text-gray-700 mb-1">
                첨부파일
              </div>
              <MobileAttachmentBox ref={attachmentRef} />
            </div>
          </div>
        </form>

        {/* 하단 버튼 */}
        <div className="px-4 py-3 border-t flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-cancel">
            취소
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            className="btn-save disabled:opacity-60"
          >
            {loading ? "저장 중..." : "저장"}
          </button>
        </div>

        {/* 회사 검색 모달 */}
        {companyModalOpen && (
          <CompanySearch
            isOpen={companyModalOpen}
            onClose={() => setCompanyModalOpen(false)}
            onSelect={handleCompanySelect}
          />
        )}
      </div>
    </div>
  );
}
